import { useState, type ReactNode } from 'react';
import { STICKY_COLORS, type ShapeKind } from '@vellum/core';
import { app, type ToolId } from '../app.ts';
import { useApp } from '../hooks.ts';
import { Icons } from './icons.tsx';
import { command, formatKeys, insertTemplate } from '../commands.ts';

interface ToolDef {
  id: ToolId;
  label: string;
  icon: ReactNode;
  cmd: string;
}

const T = (id: ToolId, label: string, icon: ReactNode): ToolDef => ({ id, label, icon, cmd: `tool.${id}` });

const DESIGN_GROUPS: ToolDef[][] = [
  [T('select', 'Move', Icons.select), T('lasso', 'Lasso', Icons.lasso), T('hand', 'Hand', Icons.hand)],
  [T('frame', 'Frame', Icons.frame), T('section', 'Section', Icons.section)],
  [T('rect', 'Rectangle', Icons.rect), T('ellipse', 'Ellipse', Icons.ellipse), T('line', 'Line', Icons.line), T('arrow', 'Arrow', Icons.arrow), T('polygon', 'Polygon', Icons.polygon), T('star', 'Star', Icons.star), T('image', 'Place image', Icons.image)],
  [T('pen', 'Pen', Icons.pen), T('pencil', 'Pencil', Icons.pencil)],
  [T('text', 'Text', Icons.text)],
  [T('sticky', 'Sticky note', Icons.sticky), T('connector', 'Connector', Icons.connector)],
  [T('comment', 'Comment', Icons.comment)],
];

const BOARD_GROUPS: ToolDef[][] = [
  [T('select', 'Move', Icons.select), T('hand', 'Hand', Icons.hand)],
  [T('pencil', 'Marker', Icons.pencil), T('eraser', 'Eraser', Icons.eraser)],
  [T('stamp', 'Stamp', Icons.stamp)],
  [T('sticky', 'Sticky note', Icons.sticky)],
  [T('shape', 'Shape', Icons.shape)],
  [T('connector', 'Connector', Icons.connector)],
  [T('mindmap', 'Mind map', Icons.mindmap)],
  [T('text', 'Text', Icons.text)],
  [T('section', 'Section', Icons.section), T('frame', 'Frame', Icons.frame)],
  [T('rect', 'Rectangle', Icons.rect), T('ellipse', 'Ellipse', Icons.ellipse), T('arrow', 'Arrow', Icons.arrow), T('line', 'Line', Icons.line), T('image', 'Place image', Icons.image)],
  [T('comment', 'Comment', Icons.comment)],
];

const SHAPES: { kind: ShapeKind; label: string; path: string }[] = [
  { kind: 'SQUARE', label: 'Square', path: 'M3 3h14v14H3z' },
  { kind: 'ELLIPSE', label: 'Ellipse', path: 'M10 3a7 7 0 110 14 7 7 0 010-14z' },
  { kind: 'ROUNDED_RECTANGLE', label: 'Rounded', path: 'M6 3h8a3 3 0 013 3v8a3 3 0 01-3 3H6a3 3 0 01-3-3V6a3 3 0 013-3z' },
  { kind: 'DIAMOND', label: 'Diamond', path: 'M10 2l8 8-8 8-8-8z' },
  { kind: 'TRIANGLE_UP', label: 'Triangle', path: 'M10 3l8 14H2z' },
  { kind: 'TRIANGLE_DOWN', label: 'Triangle down', path: 'M2 3h16l-8 14z' },
  { kind: 'PARALLELOGRAM', label: 'Parallelogram', path: 'M6 4h12l-4 12H2z' },
  { kind: 'HEXAGON', label: 'Hexagon', path: 'M6 3h8l4 7-4 7H6l-4-7z' },
  { kind: 'STAR', label: 'Star', path: 'M10 2l2.4 5.2 5.6.6-4.2 3.8 1.2 5.6-5-2.9-5 2.9 1.2-5.6L2 7.8l5.6-.6z' },
  { kind: 'CYLINDER', label: 'Cylinder', path: 'M3 5c0-3 14-3 14 0v10c0 3-14 3-14 0z M3 5c0 3 14 3 14 0' },
  { kind: 'ARROW_RIGHT', label: 'Arrow right', path: 'M2 7h9V3l7 7-7 7v-4H2z' },
  { kind: 'ARROW_LEFT', label: 'Arrow left', path: 'M18 7H9V3l-7 7 7 7v-4h9z' },
];

export function Toolbar() {
  const tool = useApp(['tool', 'doc', 'ui'], () => app.tool);
  const kind = useApp('doc', () => app.pageKind());
  const [lastInGroup, setLast] = useState<Record<number, ToolId>>({});
  const [open, setOpen] = useState<number | null>(null);
  const [hover, setHover] = useState<{ text: string; x: number; y: number } | null>(null);
  const readOnly = useApp(['doc', 'ui'], () => app.readOnly || app.ui.devMode);
  const all = kind === 'WHITEBOARD' ? BOARD_GROUPS : DESIGN_GROUPS;
  // viewers get move/hand and comments only
  const groups = readOnly ? all.filter((g) => g.some((t) => t.id === 'select' || t.id === 'comment')) : all;
  const opts = app.toolOptions;

  return (
    <div className="toolbar" onPointerDown={(e) => e.stopPropagation()}>
      {groups.map((g, gi) => {
        const activeInGroup = g.find((t) => t.id === tool);
        const shown = activeInGroup ?? g.find((t) => t.id === lastInGroup[gi]) ?? g[0];
        return (
          <div key={gi} style={{ position: 'relative', display: 'flex' }}>
            <button
              className={`tool ${activeInGroup ? 'on' : ''}`}
              aria-label={shown.label}
              onClick={() => {
                app.setTool(shown.id);
                setOpen(null);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setOpen(open === gi ? null : gi);
              }}
              onMouseEnter={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setHover({ text: `${shown.label}  ${formatKeys(command(shown.cmd)?.keys)}`, x: r.left + r.width / 2, y: r.top - 30 });
              }}
              onMouseLeave={() => setHover(null)}
            >
              {shown.icon}
              {g.length > 1 && (
                <span
                  className="caret"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(open === gi ? null : gi);
                  }}
                >
                  ▾
                </span>
              )}
            </button>
            {open === gi && g.length > 1 && (
              <div className="tool-popover" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
                {g.map((t) => (
                  <button
                    key={t.id}
                    className="btn ghost"
                    style={{ justifyContent: 'flex-start', gap: 10 }}
                    onClick={() => {
                      setLast({ ...lastInGroup, [gi]: t.id });
                      app.setTool(t.id);
                      setOpen(null);
                    }}
                  >
                    {t.icon} <span style={{ flex: 1, textAlign: 'left' }}>{t.label}</span>
                    <span style={{ color: 'var(--text-3)' }}>{formatKeys(command(t.cmd)?.keys)}</span>
                  </button>
                ))}
              </div>
            )}
            {gi === 0 && <span className="sep" />}
          </div>
        );
      })}
      {tool === 'sticky' && (
        <div className="tool-popover">
          {Object.entries(STICKY_COLORS).map(([name, c]) => (
            <button key={name} title={name} className={`sticky-swatch ${opts.stickyColor === name ? 'on' : ''}`} style={{ background: c }} onClick={() => ((opts.stickyColor = name), app.emit('tool'))} />
          ))}
        </div>
      )}
      {tool === 'shape' && (
        <div className="tool-popover">
          {SHAPES.map((sh) => (
            <button key={sh.kind} title={sh.label} className={`icon-btn ${opts.shape === sh.kind ? 'on' : ''}`} onClick={() => ((opts.shape = sh.kind), app.emit('tool'))}>
              <svg width="20" height="20" viewBox="0 0 20 20">
                <path d={sh.path} fill="none" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </button>
          ))}
        </div>
      )}
      {tool === 'connector' && (
        <div className="tool-popover">
          {(['ELBOW', 'STRAIGHT', 'CURVED'] as const).map((t) => (
            <button key={t} className={`btn ${opts.connectorType === t ? 'primary' : 'ghost'}`} onClick={() => ((opts.connectorType = t), app.emit('tool'))}>
              {t.charAt(0) + t.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      )}
      {kind === 'WHITEBOARD' && (
        <>
          <span className="sep" />
          <button className="tool" title="Insert table" onClick={() => insertTemplate('table')}>
            {Icons.table}
          </button>
          <button className="tool" title="Insert code block" onClick={() => insertTemplate('code')}>
            {Icons.code}
          </button>
        </>
      )}
      <span className="sep" />
      <button className="tool" title="Templates" onClick={() => app.setUI({ templatesOpen: true })}>
        {Icons.templates}
      </button>
      {tool === 'stamp' && (
        <div className="tool-popover">
          {['👍', '❤️', '⭐', '✅', '❓', '🔥', '🎉', '👀', '+1', '💡'].map((e) => (
            <button key={e} className={`icon-btn ${opts.stamp === e ? 'on' : ''}`} style={{ fontSize: 18, width: 34, height: 34 }} onClick={() => ((opts.stamp = e), app.emit('tool'))}>
              {e}
            </button>
          ))}
        </div>
      )}
      {tool === 'pencil' && (
        <div className="tool-popover" style={{ alignItems: 'center' }}>
          <button className={`btn ${opts.penOpacity < 1 ? 'primary' : 'ghost'}`} onClick={() => {
            const hl = opts.penOpacity >= 1;
            Object.assign(opts, hl ? { penOpacity: 0.35, penWidth: 20, penColor: '#ffcd29' } : { penOpacity: 1, penWidth: 4, penColor: '#1e1e1e' });
            app.emit('tool');
          }}>
            Highlighter
          </button>
          {['#1e1e1e', '#f24822', '#ffa629', '#14ae5c', '#0d99ff', '#9747ff', '#ff24bd', '#ffffff'].map((c) => (
            <button key={c} className={`sticky-swatch ${opts.penColor === c ? 'on' : ''}`} style={{ background: c, borderRadius: '50%', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.15)' }} onClick={() => ((opts.penColor = c), app.emit('tool'))} />
          ))}
          <span className="sep" style={{ width: 1, height: 20, background: 'var(--border)' }} />
          {[2, 4, 8, 16].map((w) => (
            <button key={w} className={`icon-btn ${opts.penWidth === w ? 'on' : ''}`} onClick={() => ((opts.penWidth = w), app.emit('tool'))}>
              <span style={{ width: Math.min(16, w + 2), height: Math.min(16, w + 2), borderRadius: '50%', background: 'currentColor' }} />
            </button>
          ))}
        </div>
      )}
      {hover && (
        <div className="tooltip" style={{ left: hover.x, top: hover.y, transform: 'translateX(-50%)' }}>
          {hover.text}
        </div>
      )}
    </div>
  );
}
